import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  CreditCard, Loader2, Save, BanknoteIcon, Info, ShieldOff, ShieldCheck,
  Plus, Pencil, Trash2, Check, X, Wifi, MapPin,
} from 'lucide-react';
import {
  getPaymentConfig, getManualPaymentSettings, saveManualPaymentSettings,
  getPrivacySettings, savePrivacySettings,
  getHotelAmenities, addHotelAmenity, updateHotelAmenity, deleteHotelAmenity,
  getNearByAttractions, addNearByAttraction, updateNearByAttraction, deleteNearByAttraction,
} from '../services/adminService';
import { useAuth } from '../context/AuthContext';

const EMPTY_MANUAL = {
  instructions: '', bankName: '', bankAccount: '',
  bkashNumber: '', nagadNumber: '', allowAdvance: false,
  advancePercent: '', verifyLabel: '',
};
const EMPTY_PRIVACY = { isEnabled: false, bookingTrigger: '', bookingCap: '', ticketTrigger: '', ticketCap: '' };
const EMPTY_AMENITY = { name: '', description: '' };
const EMPTY_ATTRACTION = { attractionName: '', attractionDistance: '' };

// ── Inline list manager (shared by Amenities + Attractions) ──────────────────
function InlineList({ items, fields, onAdd, onUpdate, onDelete, isOwner }) {
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(() => Object.fromEntries(fields.map(f => [f.key, ''])));
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleAdd = async () => {
    if (!addForm[fields[0].key]?.trim()) return;
    setSaving(true);
    try {
      await onAdd(addForm);
      setAdding(false);
      setAddForm(Object.fromEntries(fields.map(f => [f.key, ''])));
    } finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm(Object.fromEntries(fields.map(f => [f.key, item[f.key] || ''])));
  };

  const handleUpdate = async () => {
    if (!editForm[fields[0].key]?.trim()) return;
    setSaving(true);
    try {
      await onUpdate(editId, editForm);
      setEditId(null);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try { await onDelete(id); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="space-y-3">
      {/* list */}
      {items.length === 0 && !adding && (
        <p className="text-slate-500 text-sm text-center py-4">No items yet. Add one below.</p>
      )}
      {items.map(item => (
        <div key={item.id} className="bg-slate-700/40 rounded-lg border border-slate-600">
          {editId === item.id ? (
            <div className="p-3 space-y-2">
              {fields.map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-slate-400 text-xs">{f.label}</Label>
                  <Input
                    value={editForm[f.key]}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white h-8 text-sm"
                    placeholder={f.placeholder}
                  />
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={handleUpdate} disabled={saving} className="bg-green-600 hover:bg-green-700 h-7 text-xs px-3">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" />Save</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-7 text-xs text-slate-400 hover:text-white px-3">
                  <X className="h-3 w-3 mr-1" />Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between px-3 py-2 gap-3">
              <div className="min-w-0">
                <p className="text-slate-200 text-sm font-medium truncate">{item[fields[0].key]}</p>
                {fields[1] && item[fields[1].key] && (
                  <p className="text-slate-400 text-xs mt-0.5 truncate">{item[fields[1].key]}</p>
                )}
              </div>
              {isOwner && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(item)} className="p-1.5 rounded hover:bg-slate-600 text-slate-400 hover:text-white transition-colors">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="p-1.5 rounded hover:bg-red-900/40 text-slate-400 hover:text-red-400 transition-colors"
                  >
                    {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* add form */}
      {adding ? (
        <div className="bg-slate-700/30 rounded-lg border border-slate-600 p-3 space-y-2">
          {fields.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-slate-400 text-xs">{f.label}</Label>
              <Input
                value={addForm[f.key]}
                onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white h-8 text-sm"
                placeholder={f.placeholder}
                autoFocus={f === fields[0]}
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleAdd} disabled={saving} className="bg-green-600 hover:bg-green-700 h-7 text-xs px-3">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" />Add</>}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)} className="h-7 text-xs text-slate-400 hover:text-white px-3">
              <X className="h-3 w-3 mr-1" />Cancel
            </Button>
          </div>
        </div>
      ) : (
        isOwner && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            className="w-full border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-slate-400 h-8 text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add New
          </Button>
        )
      )}
    </div>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────────
const Settings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const isOwner = user?.role === 'HotelHead' || user?.role === 'Admin';

  // Payment
  const [paymentMode, setPaymentMode] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL);
  const [manualSaving, setManualSaving] = useState(false);

  // Privacy
  const [privacyForm, setPrivacyForm] = useState(EMPTY_PRIVACY);
  const [privacySaving, setPrivacySaving] = useState(false);

  // Amenities
  const [amenities, setAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(true);

  // Attractions
  const [attractions, setAttractions] = useState([]);
  const [attractionsLoading, setAttractionsLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setConfigLoading(true);
    setAmenitiesLoading(true);
    setAttractionsLoading(true);
    try {
      const calls = [getPaymentConfig(), getManualPaymentSettings()];
      if (isOwner) calls.push(getPrivacySettings());
      const [config, settings, privacy] = await Promise.all(calls);
      setPaymentMode(config.paymentMode ?? null);
      if (settings && Object.keys(settings).length > 0) {
        setManualForm({
          instructions: settings.instructions || '',
          bankName: settings.bankName || '',
          bankAccount: settings.bankAccount || '',
          bkashNumber: settings.bkashNumber || '',
          nagadNumber: settings.nagadNumber || '',
          allowAdvance: settings.allowAdvance || false,
          advancePercent: settings.advancePercent != null ? String(settings.advancePercent) : '',
          verifyLabel: settings.verifyLabel || '',
        });
      }
      if (privacy) {
        setPrivacyForm({
          isEnabled:      privacy.isEnabled ?? false,
          bookingTrigger: privacy.bookingTrigger != null ? String(privacy.bookingTrigger) : '',
          bookingCap:     privacy.bookingCap     != null ? String(privacy.bookingCap)     : '',
          ticketTrigger:  privacy.ticketTrigger  != null ? String(privacy.ticketTrigger)  : '',
          ticketCap:      privacy.ticketCap      != null ? String(privacy.ticketCap)      : '',
        });
      }
    } catch { /* non-fatal */ }
    finally { setConfigLoading(false); }

    try { setAmenities(await getHotelAmenities()); } catch { /* non-fatal */ }
    finally { setAmenitiesLoading(false); }

    try { setAttractions(await getNearByAttractions()); } catch { /* non-fatal */ }
    finally { setAttractionsLoading(false); }
  }, [isOwner]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Amenity handlers
  const handleAddAmenity = async (form) => {
    await addHotelAmenity({ name: form.name.trim(), description: form.description?.trim() || '' });
    toast({ title: 'Amenity added' });
    setAmenities(await getHotelAmenities());
  };
  const handleUpdateAmenity = async (id, form) => {
    await updateHotelAmenity(id, { name: form.name.trim(), description: form.description?.trim() || '' });
    toast({ title: 'Amenity updated' });
    setAmenities(await getHotelAmenities());
  };
  const handleDeleteAmenity = async (id) => {
    await deleteHotelAmenity(id);
    toast({ title: 'Amenity deleted' });
    setAmenities(prev => prev.filter(a => a.id !== id));
  };

  // Attraction handlers
  const handleAddAttraction = async (form) => {
    await addNearByAttraction({ attractionName: form.attractionName.trim(), attractionDistance: form.attractionDistance?.trim() || '' });
    toast({ title: 'Attraction added' });
    setAttractions(await getNearByAttractions());
  };
  const handleUpdateAttraction = async (id, form) => {
    await updateNearByAttraction(id, { attractionName: form.attractionName.trim(), attractionDistance: form.attractionDistance?.trim() || '' });
    toast({ title: 'Attraction updated' });
    setAttractions(await getNearByAttractions());
  };
  const handleDeleteAttraction = async (id) => {
    await deleteNearByAttraction(id);
    toast({ title: 'Attraction deleted' });
    setAttractions(prev => prev.filter(a => a.id !== id));
  };

  const handleSaveManual = async (e) => {
    e.preventDefault();
    setManualSaving(true);
    try {
      await saveManualPaymentSettings({
        instructions: manualForm.instructions || null,
        bankName: manualForm.bankName || null,
        bankAccount: manualForm.bankAccount || null,
        bkashNumber: manualForm.bkashNumber || null,
        nagadNumber: manualForm.nagadNumber || null,
        allowAdvance: manualForm.allowAdvance,
        advancePercent: manualForm.advancePercent ? parseFloat(manualForm.advancePercent) : null,
        verifyLabel: manualForm.verifyLabel || null,
      });
      toast({ title: 'Payment settings saved' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally { setManualSaving(false); }
  };

  const handleSavePrivacy = async (e) => {
    e.preventDefault();
    setPrivacySaving(true);
    try {
      await savePrivacySettings({
        isEnabled:      privacyForm.isEnabled,
        bookingTrigger: privacyForm.bookingTrigger ? parseInt(privacyForm.bookingTrigger) : null,
        bookingCap:     privacyForm.bookingCap     ? parseInt(privacyForm.bookingCap)     : null,
        ticketTrigger:  privacyForm.ticketTrigger  ? parseInt(privacyForm.ticketTrigger)  : null,
        ticketCap:      privacyForm.ticketCap      ? parseInt(privacyForm.ticketCap)      : null,
      });
      toast({ title: 'Privacy settings saved' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally { setPrivacySaving(false); }
  };

  const setField = (field) => (e) => setManualForm(prev => ({ ...prev, [field]: e.target.value }));
  const setPrivacyField = (field) => (e) => setPrivacyForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 mt-1">Configure hotel system preferences</p>
      </div>

      {/* ── Hotel Amenities ── */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wifi className="h-5 w-5 text-blue-400" />
            Hotel Amenities
          </CardTitle>
          <p className="text-slate-400 text-sm">Features and amenities shown on your hotel's public page (e.g. WiFi, AC, Balcony)</p>
        </CardHeader>
        <CardContent>
          {amenitiesLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : (
            <InlineList
              items={amenities}
              fields={[
                { key: 'name', label: 'Name', placeholder: 'e.g. WiFi, Swimming Pool, AC' },
                { key: 'description', label: 'Description', placeholder: 'e.g. High-speed WiFi in all rooms' },
              ]}
              onAdd={handleAddAmenity}
              onUpdate={handleUpdateAmenity}
              onDelete={handleDeleteAmenity}
              isOwner={isOwner}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Nearby Attractions ── */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MapPin className="h-5 w-5 text-amber-400" />
            Nearby Attractions
          </CardTitle>
          <p className="text-slate-400 text-sm">Points of interest shown on your hotel's public page</p>
        </CardHeader>
        <CardContent>
          {attractionsLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : (
            <InlineList
              items={attractions}
              fields={[
                { key: 'attractionName', label: 'Name', placeholder: 'e.g. Cox\'s Bazar Beach, Shopping Mall' },
                { key: 'attractionDistance', label: 'Distance', placeholder: 'e.g. 2 km, 10 min walk' },
              ]}
              onAdd={handleAddAttraction}
              onUpdate={handleUpdateAttraction}
              onDelete={handleDeleteAttraction}
              isOwner={isOwner}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Manual Payment Settings ── */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-400" />
            Manual Payment Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {configLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading payment configuration...</span>
            </div>
          ) : paymentMode === 1 ? (
            <div className="flex items-start gap-3 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
              <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-300 text-sm font-medium">Automatic Payment Mode</p>
                <p className="text-slate-400 text-xs mt-0.5">This hotel is set to Automatic payment by TexoHotel. Manual payment settings are not applicable.</p>
              </div>
            </div>
          ) : paymentMode === null ? (
            <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
              <Info className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 text-sm font-medium">Payment Not Configured</p>
                <p className="text-slate-400 text-xs mt-0.5">No payment mode has been set by TexoHotel yet.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSaveManual} className="space-y-5">
              <div className="flex items-start gap-3 p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                <BanknoteIcon className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-green-300 text-xs">Manual payment is active. Guests will see these instructions before booking and enter a payment reference.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200">Payment Instructions <span className="text-slate-500 font-normal">(shown to guests)</span></Label>
                <Textarea value={manualForm.instructions} onChange={setField('instructions')} className="bg-slate-700 border-slate-600 text-white" rows={3} placeholder="e.g. Please send payment to our bKash account..." />
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-300">Bank Account</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Bank Name</Label>
                    <Input value={manualForm.bankName} onChange={setField('bankName')} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. Dutch Bangla Bank" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Account Number</Label>
                    <Input value={manualForm.bankAccount} onChange={setField('bankAccount')} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. 1234567890123" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-300">Mobile Banking</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">bKash Number</Label>
                    <Input value={manualForm.bkashNumber} onChange={setField('bkashNumber')} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. 01XXXXXXXXX" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Nagad Number</Label>
                    <Input value={manualForm.nagadNumber} onChange={setField('nagadNumber')} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. 01XXXXXXXXX" />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-slate-200">Verification Field Label</Label>
                <Input value={manualForm.verifyLabel} onChange={setField('verifyLabel')} className="bg-slate-700 border-slate-600 text-white" placeholder="e.g. Enter your bKash Transaction ID" />
                <p className="text-xs text-slate-500">This is the label guests see on the reference input field.</p>
              </div>

              <div className="space-y-3 p-4 bg-slate-700/40 rounded-lg border border-slate-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-200">Allow Advance Payment</p>
                    <p className="text-xs text-slate-400">Let guests pay a percentage now and the rest at check-in</p>
                  </div>
                  <button type="button" onClick={() => setManualForm(p => ({ ...p, allowAdvance: !p.allowAdvance }))} className={`relative w-11 h-6 rounded-full transition-colors ${manualForm.allowAdvance ? 'bg-green-600' : 'bg-slate-600'}`}>
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${manualForm.allowAdvance ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
                {manualForm.allowAdvance && (
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Advance Percentage (%)</Label>
                    <Input type="number" min="1" max="99" step="1" value={manualForm.advancePercent} onChange={setField('advancePercent')} className="bg-slate-700 border-slate-600 text-white w-32" placeholder="e.g. 30" />
                    <p className="text-xs text-slate-500">Guest pays this % upfront. Balance collected at check-in.</p>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={manualSaving} className="bg-green-600 hover:bg-green-700">
                {manualSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Payment Settings</>}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* ── Privacy Mode (HotelHead / Admin only) ── */}
      {isOwner && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {privacyForm.isEnabled ? <ShieldOff className="h-5 w-5 text-amber-400" /> : <ShieldCheck className="h-5 w-5 text-slate-400" />}
              Privacy Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSavePrivacy} className="space-y-5">
              <p className="text-slate-400 text-sm">When enabled, staff see only the most recent <strong className="text-slate-200">N records</strong> once data exceeds your trigger threshold.</p>

              <div className="flex items-center justify-between p-4 bg-slate-700/40 rounded-lg border border-slate-600">
                <div>
                  <p className="text-sm font-medium text-slate-200">Enable Privacy Mode</p>
                  <p className="text-xs text-slate-400 mt-0.5">{privacyForm.isEnabled ? 'Caps are active — HotelHead always sees everything.' : 'Off — all staff see real data.'}</p>
                </div>
                <button type="button" onClick={() => setPrivacyForm(p => ({ ...p, isEnabled: !p.isEnabled }))} className={`relative w-12 h-6 rounded-full transition-colors ${privacyForm.isEnabled ? 'bg-amber-500' : 'bg-slate-600'}`}>
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${privacyForm.isEnabled ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              {privacyForm.isEnabled && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Cap Rules</p>
                  {[
                    { label: 'Bookings', trigger: 'bookingTrigger', cap: 'bookingCap' },
                    { label: 'Ticket Orders', trigger: 'ticketTrigger', cap: 'ticketCap' },
                  ].map(({ label, trigger, cap }) => (
                    <div key={label} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600 space-y-3">
                      <p className="text-sm font-medium text-slate-200">{label}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">If actual exceeds</Label>
                          <Input type="number" min="1" value={privacyForm[trigger]} onChange={setPrivacyField(trigger)} placeholder="e.g. 3000" className="bg-slate-700 border-slate-600 text-white" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Show only (most recent)</Label>
                          <Input type="number" min="1" value={privacyForm[cap]} onChange={setPrivacyField(cap)} placeholder="e.g. 1000" className="bg-slate-700 border-slate-600 text-white" />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">If actual {label.toLowerCase()} &gt; {privacyForm[trigger] || '?'}, staff see only the latest {privacyForm[cap] || '?'}.</p>
                    </div>
                  ))}
                </div>
              )}

              <Button type="submit" disabled={privacySaving} className="bg-amber-600 hover:bg-amber-700">
                {privacySaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Privacy Settings</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
